import React from 'react';
import NProgress from 'nprogress';
import { EyeIcon, Input, Modal } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { signOut } from 'next-auth/react';
import { useRouter } from 'next/router';
import {
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_WITHOUT_TOKEN,
  SIGN_OUT,
} from '../../../graphql/mutation';
import { useNotify, useUser } from '../../../hooks';
import Button from '../Button/Button';

interface ChangePasswordModalProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  firstLogin?: boolean;
}

function ChangePasswordModal({
  isOpen,
  setOpen,
  firstLogin = false,
}: ChangePasswordModalProps) {
  const notify = useNotify();
  const router = useRouter();
  const [user] = useUser();

  const [disabled, setDisabled] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [rePassword, setRePassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error1, setError1] = React.useState('');
  const [error2, setError2] = React.useState('');

  const [changePasswordWithoutToken] = useMutation<{
    changePasswordWithoutToken: {
      success: boolean;
      err: string;
    };
  }>(CHANGE_PASSWORD_WITHOUT_TOKEN);

  const [changePasswordMutation] = useMutation<{
    changePassword: {
      success: boolean;
      err: string;
    };
  }>(CHANGE_PASSWORD);

  const [signOutMutation] = useMutation<{
    signOut: {
      success: boolean;
    };
  }>(SIGN_OUT);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      NProgress.start();
      if (disabled) return;
      setDisabled(true);

      if (password !== rePassword) {
        notify('Las contraseñas no coinciden', 'error');
        return;
      }

      if (error1 || error2) {
        notify('Por favor, corrija los errores', 'error');
        return;
      }
      if (firstLogin) {
        changePasswordOnFirstLogin();
      } else if (router.query.token) {
        changePassword();
      }
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const changePasswordOnFirstLogin = async () => {
    const { data } = await changePasswordWithoutToken({
      variables: {
        data: {
          password,
          userId: user?._id,
        },
      },
    });
    if (data?.changePasswordWithoutToken?.success) {
      notify('Contraseña cambiada con éxito', 'success');
      setOpen(false);
      signOutMutation();
      signOut({ callbackUrl: `/sign-in` });
    } else {
      notify(data?.changePasswordWithoutToken?.err, 'error');
    }
  };

  const changePassword = async () => {
    const { data } = await changePasswordMutation({
      variables: {
        data: {
          password,
          token: router.query.token as string,
        },
      },
    });
    if (data?.changePassword?.success) {
      notify('Contraseña cambiada con éxito', 'success');
      setOpen(false);
    } else {
      notify(data?.changePassword?.err, 'error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
      setError1(validatePassword(value));
    }
    if (name === 'rePassword') {
      setRePassword(value);
      setError2(validatePassword(value));
    }
  };

  const validatePassword = (passwordItem: string) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_])[A-Za-z\d\W_]{8,}$/;

    if (passwordItem.length < 12) {
      return 'La contraseña debe contener al menos 12 caracteres';
    }
    if (!passwordRegex.test(passwordItem)) {
      return 'La contraseña debe contener al menos una letra mayúscula, una letra minúscula, un número y un carácter especial';
    }

    return '';
  };

  return (
    <Modal isOpen={isOpen} setOpen={setOpen} className="md:max-w-[40%]">
      <form method="POST" onSubmit={onSubmit}>
        <div className="bg-white rounded-lg ">
          <Modal.Title title="Cambio de contraseña" setIsOpen={setOpen} />
          <div className="flex flex-col p-6">
            {/* description */}
            <span className="text-text-gray ">
              Una vez cambie su contraseña deberá iniciar sesión con su nueva
              contraseña
            </span>
            <div className="w-full">
              <div className="">
                <Input
                  label="Contraseña"
                  placeholder="********"
                  name="password"
                  onChange={handleChange}
                  type={showPassword ? 'text' : 'password'}
                  rightIcon={
                    <EyeIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPassword(!showPassword);
                      }}
                    />
                  }
                />
              </div>
              {error1 && <p className="mt-2 text-xs text-red-600">{error1}</p>}
            </div>
            <div className="w-full my-4">
              <div className="">
                <Input
                  label="Confirmar contraseña"
                  placeholder="********"
                  name="rePassword"
                  onChange={handleChange}
                  type={showPassword ? 'text' : 'password'}
                  rightIcon={
                    <EyeIcon
                      className="w-5 h-5 cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowPassword(!showPassword);
                      }}
                    />
                  }
                />
              </div>
              {error2 && <p className="mt-2 text-xs text-red-600">{error2}</p>}
            </div>
            <Button className="mt-5">Cambiar contraseña</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default ChangePasswordModal;
